import { put, select } from 'redux-saga/effects';

import { isBoolean } from '@elab/core/utils';
import {
    formFieldsConstants as CONSTANTS,
    registrationErrorType,
    registrationServerError,
    validRegistrationValidationForm,
} from '@elab/core/utils/constants';
import { dateToString, formatToCustom, formatToISO } from '@elab/core/utils/DateUtils';
import { actions as authActions } from '@elab/core/store/auth';
import { actions as globalActions } from '@elab/core/store/global';
import { selectors as configSelectors } from '@elab/core/store/config';

export function dtoBuilder(data, dto = {}) {
    Object.values(data).forEach(field => {
        let { dtoFields, type = '', value, fields, maxNestedBlocks = 1, dateFormat = '' } = field;
        const isNestedForm = fields && maxNestedBlocks === 1;
        dtoFields = dtoFields || [];

        if (
            !dtoFields ||
            value === undefined ||
            (value === null && (!isNestedForm || !dtoFields.length))
        ) {
            return;
        }

        if (value && isNestedForm) {
            value = dtoBuilder(value[0], {});
        } else if (value && fields) {
            value = value.map(subField => dtoBuilder(subField, {}));
        }

        if (type === CONSTANTS.date) {
            value = dateFormat ? formatToCustom(value, dateFormat) : formatToISO(value);
        }

        if (dtoFields.length) {
            dtoFields.forEach(dtoField => {
                value = value === null ? false : value;
                fromStringToObject(
                    isNestedForm && typeof value === 'object' ? '' : dtoField,
                    value,
                    dto,
                );

                if (isNestedForm) {
                    fromStringToObject(dtoField, !!value, dto);
                }
            });
        } else if (value && typeof value === 'object') {
            fromStringToObject('', value, dto);
        }
    });

    return dto;
}

export function fromStringToObject(strObjNotation = '', value, dto = {}) {
    const objectKeys = strObjNotation.split('.');
    if (!objectKeys) {
        return dto;
    }

    if (!objectKeys[0] && typeof value === 'object') {
        Object.entries(value).forEach(([key, innerValue]) => {
            fromStringToObject(key, innerValue, dto);
        });
        return dto;
    }

    let subObj = dto;
    const lastEl = objectKeys.length - 1;

    objectKeys.forEach((key, index) => {
        if (index === lastEl) {
            subObj[key] =
                typeof subObj[key] === 'object'
                    ? {
                          ...subObj[key],
                          ...value,
                      }
                    : value;
            return;
        }

        subObj[key] = subObj[key] || {};
        subObj = subObj[key];
    });
    return dto;
}

export function getFieldPathFromDtoName(dtoName = '', data = {}) {
    if (typeof data !== 'object') {
        return '';
    }
    const reg = /(.+?)\[([0-9]+)]\.(.+)/;
    const result = reg.exec(dtoName);
    let fieldPath;
    let base = dtoName;
    let index;
    let child;

    if (result) {
        [, base, index, child] = result;
    }

    let fields = Object.values(data);

    // Find full match
    let field = fields.find(({ dtoFields = [] }) => {
        return dtoFields.find(dtoField => base === dtoField);
    });

    if (field) {
        fieldPath = field.name;
    } else {
        // Find partial match
        fields = fields.filter(({ dtoFields = [], value, maxNestedBlocks = 1 }) => {
            return (
                maxNestedBlocks === 1 &&
                value &&
                (!dtoFields.length ||
                    dtoFields.some(dtoField => base.startsWith(`${dtoField}.`) || !dtoField))
            );
        });

        let subPart = '';
        search: for (field of fields) {
            const fieldDtoFields = field.dtoFields || [];
            if (!fieldDtoFields.length) {
                subPart = getFieldPathFromDtoName(base, field.value[0]);
                if (subPart) {
                    break;
                } else {
                    continue;
                }
            }

            for (const dtoField of fieldDtoFields) {
                subPart = getFieldPathFromDtoName(
                    base.substring(dtoField.length + (dtoField ? 1 : 0)),
                    field.value[0],
                );
                if (subPart) {
                    break search;
                }
            }
        }

        const selectedIndex = fields.indexOf(field);
        fieldPath =
            subPart && selectedIndex >= 0 ? `${fields[selectedIndex].name}.value.0.${subPart}` : '';
    }

    if (!fieldPath) {
        return '';
    }
    return (
        fieldPath +
        (index ? `.value.${index}.${getFieldPathFromDtoName(child, field.value[index])}` : '')
    );
}

export const metadataConverter = (acc, metadataStep) => ({
    ...acc,
    ...Object.entries(metadataStep.fields).reduce(
        (acc, [name, fieldMetadata]) => ({
            [name]: {
                ...fieldMetadata,
                initialConstraints: fieldMetadata.constraints || [],
                value: isBoolean(fieldMetadata.defaultValue)
                    ? JSON.parse(fieldMetadata.defaultValue)
                    : // defaultValue for DATE field must be converted from date to string
                    fieldMetadata.defaultValue
                    ? fieldMetadata.type === CONSTANTS.date
                        ? dateToString(fieldMetadata.defaultValue, fieldMetadata.placeholder)
                        : fieldMetadata.defaultValue
                    : null,
                required: (fieldMetadata.constraints || []).some(validator =>
                    ['required', 'equalsTo'].includes(validator.type),
                ),
                error: '',
                focused: false,
                touched: false,
            },
            ...acc,
        }),
        {},
    ),
});

export function cleanPhoneNumber(dto) {
    const keys = Object.keys(dto);

    const phoneKey = dto.mobileNumber ? 'mobileNumber' : 'phone';
    if (dto[phoneKey]) {
        dto[phoneKey] = dto[phoneKey].replace(/\D/g, '');
    }

    keys.forEach(key => {
        if (typeof dto[key] === 'object') {
            dto[key] = cleanPhoneNumber(dto[key]);
        }
    });

    return dto;
}

export function afterInitializeReduce(state) {
    return {
        ...state,
        data: {
            ...state.data,
            ...(state.data.dateOfBirth
                ? {
                      dateOfBirth: {
                          ...state.data.dateOfBirth,
                          constraints: (
                              (state.data.dateOfBirth && state.data.dateOfBirth.constraints) ||
                              []
                          ).filter(constraint => constraint.type !== 'ageVerification'),
                          dialogConstraints: (
                              (state.data.dateOfBirth && state.data.dateOfBirth.constraints) ||
                              []
                          ).filter(constraint => ['ageVerification'].indexOf(constraint.type) >= 0),
                      },
                  }
                : {}),
        },
    };
}

export function* validateEmailAlreadyExist(errors, uid, storeName) {
    const shouldCheckEmailAlreadyExist = yield select(configSelectors.shouldCheckEmailAlreadyExist);

    if (!shouldCheckEmailAlreadyExist) {
        return undefined;
    }
    if (
        validRegistrationValidationForm.indexOf(storeName) > -1 &&
        errors.findIndex(item => item.id === registrationServerError.EMAIL_ALREADY_EXIST) > -1
    ) {
        if (storeName === 'registrationForm') {
            yield put(
                globalActions.redirect(
                    `/login?error=true&errortype=${registrationErrorType.ALREADY_EXIST}&uid=${uid}`,
                ),
            );
        } else {
            // for storeName === 'checkout-registration'
            yield put(authActions.setDataField('username', uid));
            yield put({
                type: 'checkout-auth-options.SET_DATA_FIELD_VALUE',
                payload: {
                    name: 'checkoutOptions',
                    value: 'checkout-login',
                },
            });
        }

        yield put(
            authActions.setError({
                errorType: registrationErrorType.ALREADY_EXIST,
            }),
        );
        return false;
    }
    return true;
}
